import {Component, OnInit} from "@angular/core";
import {byNumeric} from "iso-country-codes";
import {MicroPaymentService} from "../../services/micro-payment.service";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {MicroPaymentSession, MicroPaymentTransaction} from "../../../dto/invoice";
import {getTableFilters} from "../../../helpers/storage-helper";
import {PrevNextIdModel} from "../../../models/prev-next-id-model";
import {Customer} from "../../../dto/customer";
import {ContentRowField} from "../../../shared/content-row/content-row.component";
import {toDateString} from "../../../providers/app-date-adapter";
import {
  getIdentStatusColor,
  getIdentStatusIcon,
  getIdentStatusText,
  getPepStatusColor,
  getPepStatusText
} from "../../../helpers/pep-status-helper";
import {AddressChangeStatus, AddressStatus} from "../../../dto/ident-status";
import {Company} from "../../../dto/company";

@Component({
  selector: 'app-micro-payment-session-detail',
  templateUrl: './micro-payment-session-detail.component.html',
  styleUrls: ['./micro-payment-session-detail.component.css']
})
export class MicroPaymentSessionDetailComponent implements OnInit {

  public sessionId: number;
  public isLoading = false;
  public session: MicroPaymentSession|null;
  public prevNextIds: PrevNextIdModel|null;
  public customer: Customer|null;
  public company: Company | null;

  constructor(
      private microPaymentService: MicroPaymentService,
      private route: ActivatedRoute,
      private router: Router,
  ) { }

  ngOnInit(): void {

    this.route.params.subscribe((params: Params) => {
      this.loadData();
    });

  }

  returnBack() {
    this.router.navigate(['/loads/micropayment']).then();
  }


  loadData() {
    this.sessionId = parseInt(this.route.snapshot.paramMap.get('id'));

    this.isLoading = true;
    this.microPaymentService.get(this.sessionId).subscribe(data => {
      this.session = data;
      this.getPrevNextIds(this.sessionId);
      this.customer = this.session.microPaymentCustomer?.customer;
      this.company = this.session.microPaymentCustomer?.company;
      this.isLoading = false;
    });
  }

  getPrevNextIds(id: number) {
    const filters = this.getTableFilters();
    this.microPaymentService.getPrevNextIds(filters, id).subscribe(result => {
      this.prevNextIds = result;
    });
  }


  getTableFilters() {
    return getTableFilters('microPaymentTable');
  }



  gotoPrev() {
    this.router.navigate(['/loads/micropayment/' + this.prevNextIds.prevId], ).then();
  }

  gotoNext() {
    this.router.navigate(['/loads/micropayment/' + this.prevNextIds.nextId], ).then();


  }


  fieldsCustomer = [
    { header: 'Title', cell: (row: Customer) => `${row.title}` },
    { header: 'First name', cell: (row: Customer) => `${row.firstName || ''}` } as ContentRowField<Customer>,
    { header: 'Last name', cell: (row: Customer) => `${row.lastName || ''}` } as ContentRowField<Customer>,
    { header: 'Address', cell: (row: Customer) => `${row.address || ''}` } as ContentRowField<Customer>,
    { header: 'City', cell: (row: Customer) => `${row.city || ''}` } as ContentRowField<Customer>,
    { header: 'Zip code', cell: (row: Customer) => `${row.zipCode || ''}` } as ContentRowField<Customer>,
    { header: 'Email', cell: (row: Customer) => `${row.email || ''}` } as ContentRowField<Customer>,
    { header: 'Phone', cell: (row: Customer) => `${row.phone || ''}` } as ContentRowField<Customer>,
    { header: 'Date of birth', cell: (row: Customer) => `${toDateString(row.dob) || ''}` } as ContentRowField<Customer>,
    { header: 'Country', cellFlag: (row: Customer) => byNumeric[row.countryIsoCode.toString().padStart(3, '0')].alpha2 || null, cell: (row: Customer) => `${row.countryName || ''}` } as ContentRowField<Customer>,

    {
      header: 'Ident',
      iconColor: row => getIdentStatusColor(row.identStatus),
      icon: (row: Customer) => getIdentStatusIcon(row.identStatus),
      cell: row => getIdentStatusText(row.identStatus),
    } as ContentRowField<Customer>,
    {
      header: 'PEP Status',
      iconColor: (row: Customer) => getPepStatusColor(row.pepStatus), icon: (row: Customer) => 'perm_identity',
      cell: (row: Customer) => getPepStatusText(row.pepStatus),
    },
    {
      header: 'Address Status',
      iconColor: (row: Customer) => {

        if (row.addressStatus == AddressStatus.Confirmed) {
          return '#8bc34a';
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return '#4a82c3';
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return '#684ac3';
        }

        return '#d04848';
      },
      icon: (row: Customer) => {
        if (row.addressStatus == AddressStatus.Confirmed) {
          return 'place';
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return 'not_listed_location';
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return 'not_listed_location';
        }

        return 'not_listed_location';
      }, cell: (row: Customer) => {
        if (row.addressStatus == AddressStatus.Confirmed) {
          return 'Confirmed';
        }
        if (row.addressStatus == AddressStatus.WaitingForDocuments) {
          return 'Waiting for documents';
        }
        if (row.addressStatus == AddressStatus.PendingCheck) {
          return 'Pending check';
        }

        return 'Unconfirmed';
      }},

    {
      header: 'Address Change Status',
      iconColor: (row: Customer) => {

        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return '#4a82c3';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return '#684ac3';
        }

        return '#ffffff';
      },
      icon: (row: Customer) => {
        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return 'not_listed_location';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return 'not_listed_location';
        }

        return '';
      }, cell: (row: Customer) => {
        if (row.addressChangeStatus == AddressChangeStatus.WaitingForDocuments) {
          return 'Waiting for documents';
        }
        if (row.addressChangeStatus == AddressChangeStatus.PendingCheck) {
          return 'Pending check';
        }

        return '';
      }},



    { header: 'Docs', cell: (row: Customer) => `${row.docsSuccess ? 'OK' : 'missing or declined'}` } as ContentRowField<Customer>,
    { header: 'IDV', cell: (row: Customer) => `${row.idvSuccess ? 'OK' : 'missing or declined'}` } as ContentRowField<Customer>,
  ] as ContentRowField<Customer>[];


  fieldsCompany = [
    {header: 'Name', cell: (row: Company) => `${row.name}`} as ContentRowField<Company>,
    {header: 'Brand Name', cell: (row: Company) => `${row.brandName || ''}`},
    {header: 'Director name', cell: (row: Company) => `${row.directorFirstName || ''} ${row.directorName || ''}`},
    {header: 'Director job title', cell: (row: Company) => `${row.directorJobTitle || ''}`},
    {header: 'Phone', cell: (row: Company) => `${row.phone || ''}`},
    {header: 'Email', cell: (row: Company) => `${row.email || ''}`},
    {header: 'Website URL', isExternalLink: (row: Company) => `${row.websiteUrl || ''}`, link: (row: Company) => `${row.websiteUrl || ''}`, cell: (row: Company) => `${row.websiteUrl || ''}`},
    {header: 'Support Email', cell: (row: Company) => `${row.supportEmail || ''}`},
    {header: 'Support Phone', cell: (row: Company) => `${row.supportPhone || ''}`},
    {header: 'Country', cell: (row: Company) => `${row.countryName || ''}`},
    {header: 'City', cell: (row: Company) => `${row.city || ''}`},
    {header: 'Address', cell: (row: Company) => `${row.address || ''}`},
    {header: 'Postcode', cell: (row: Company) => `${row.postcode || ''}`},
    {header: 'VAT reg nr', cell: (row: Company) => `${row.vatRegNr || ''}`},
    {header: 'VAT rate', cell: (row: Company) => `${row.vatRate || ''}`},

  ] as ContentRowField<Company>[];

  fieldsTransaction = [
    {header: 'Type', cell: row => `${row.type}`},
    {header: 'Date', cell: row => `${row.transactionDate}`},
    {header: 'PaidAmount', cell: row => `${row.paidAmount}`},
  ] as ContentRowField<MicroPaymentTransaction>[];


}
